// src/containers/signup/index.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'reactstrap';
import { Subtitle, Description, Titlespan2 } from '../../components/common/title/index';
import PRODUCT_CATALOG, { FALLBACK_BY_ROLE } from '../../data/signup/product-catalog';
import {
  GRADES,
  US_STATES,
  stateName,
} from '../../config/geography';
import {
  loadStateSettings,
  isStateAvailable,
  isSubjectReady,
} from '../../services/stateSettingsService';
import { gradeToken } from '../../config/plans';
import './signup.scss';

// The Subscribe buttons used to link straight to buy.stripe.com Payment Links
// pulled from PRODUCT_CATALOG. Those create anonymous Stripe sessions with no
// client_reference_id and no firebaseUid — the webhook receives the payment,
// finds no buyerUid, and grants nothing. People were paying for accounts that
// never unlocked.
//
// Now the buttons hand off to /register with the chosen combination, and
// checkout happens after the account exists. PRODUCT_CATALOG is still the
// source for titles, blurbs, and tool lists; its stripeMonthly/stripeYearly
// fields are no longer read.

// Every state code, so the counts below can be derived from live settings
// rather than a constant. The copy under the picker used to claim "Virginia and
// West Virginia are live with all four subjects" — West Virginia had no social
// studies at all. Counting what is actually switched on is the only way that
// sentence stays true as states come online.
const ALL_STATES = [...US_STATES];

const SUBJECT_KEYS = ['science', 'math', 'socialstudies'];
const SUBJECT_NAMES = { science: 'Science', math: 'Math', socialstudies: 'Social Studies' };

const byName = (a, b) => stateName(a).localeCompare(stateName(b));

// "Math & Science" / "Math, Science & Social Studies"
const joinSubjects = (keys) => {
  const names = keys.map((k) => SUBJECT_NAMES[k]);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

// The dropdown groups, built from LIVE settings.
//
// These used to be STATE_GROUPS from geography.js — constants, labelled at
// build time. Once stateSettings became the switch, a state could be turned
// on in Firestore and still sit under "Coming soon" in the picker, because
// the group it rendered under was decided before the settings ever loaded.
// Grouping from the same helpers that decide `disabled` means the label and
// the behaviour can't disagree.
//
// Before the settings arrive the helpers answer from geography.js, which now
// offers every state, so the first paint is the same as the settled one.
function buildStateGroups() {
  const full = [];
  const partial = [];
  const soon = [];
  for (const s of ALL_STATES) {
    const ready = SUBJECT_KEYS.filter((sub) => isSubjectReady(s, sub));
    if (isStateAvailable(s) && ready.length === SUBJECT_KEYS.length) full.push(s);
    else if (isStateAvailable(s) && ready.length) partial.push(s);
    else soon.push(s);
  }
  return [
    { label: 'Available now — Math, Science & Social Studies', codes: full.sort(byName) },
    { label: 'Available now — more subjects coming', codes: partial.sort(byName) },
    { label: 'Coming soon', codes: soon.sort(byName) },
  ].filter((g) => g.codes.length);
}

// ---------------------------------------------------------------------------
// Product image with a fallback that cannot loop
//
// The old <img> handled a missing file with
//     onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholder; }}
// That `onerror = null` is the plain-DOM idiom and does nothing in React —
// React's onError is its own listener, not the element's onerror property.
// So when the product image was missing AND placeholder.png was missing too,
// every failure set src to the placeholder again, which failed again, which
// set it again: an endless error/reload loop. Chrome shows that as the image
// box flickering and the page stuttering, with no picture.
//
// Most of the 51-state catalog points at images that don't exist yet
// (ak1educator.png, ga3student.png…), so every new state hit it.
//
// Now the fallbacks are a fixed list walked once, in order: the product's
// image, the role's generic image, then an inline SVG. The inline one is a
// data: URI, so it can't 404 and the chain always ends.
// ---------------------------------------------------------------------------
const BUILTIN_PLACEHOLDER = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 300">' +
    '<rect width="420" height="300" rx="18" fill="#eef0fb"/>' +
    '<circle cx="210" cy="120" r="46" fill="#c9cdf2"/>' +
    '<path d="M196 98v42a12 12 0 1 0 6 10v-34l22-6v22a12 12 0 1 0 6 10V90z" fill="#6b72c9"/>' +
    '<text x="210" y="215" text-anchor="middle" font-family="Arial, sans-serif" font-size="22" font-weight="700" fill="#4b4f8f">Smart Board Learning</text>' +
    '</svg>'
)}`;

function ProductImage({ sources, alt }) {
  const list = [...new Set((sources || []).filter(Boolean)), BUILTIN_PLACEHOLDER];
  const [index, setIndex] = useState(0);
  const src = list[Math.min(index, list.length - 1)];

  return (
    <img
      src={src}
      alt={alt}
      // Step forward only; the last entry never errors, so this stops.
      onError={() => setIndex((i) => (i < list.length - 1 ? i + 1 : i))}
    />
  );
}

// The catalog's "student" role means a student-facing plan, which a parent
// buys. The registration flow distinguishes parent from bulk student seats.
const SIGNUP_ROLE_FOR_PICKER = {
  educator: 'educator',
  student: 'parent',
  district: 'district',
};

const registerHref = ({ state, grade, role, billing }) => {
  const q = new URLSearchParams({
    state,
    grade,
    role: SIGNUP_ROLE_FOR_PICKER[role] || role,
    billing,
  });
  return `/register?${q.toString()}`;
};

// --- Options ---
//
// STATES and GRADES used to be declared right here, and GRADES was
// ['1','2','3','4','5'] — the K-5 list, still in place after the platform
// expanded to Pre-K through 12. An 8th-grade parent reaching this page found
// their child's grade simply absent, with no explanation.
//
// Both lists now come from config/geography.js. This is the same duplication
// that produced the "3" vs "3rd" bug documented at the top of that file: two
// copies of the same list in two files will disagree eventually, and the
// disagreement shows up as an account whose role string matches no route.
// One list, imported.
//
// `gradeToken` comes from config/plans.js for the same reason — the local
// copy here handled 1-5 and nothing else, so "Kindergarten" and "9th" would
// have produced catalog keys that match nothing.

// Combine to a catalog key, e.g. "va-5th-educator".
const comboKey = (st, g, r) => {
  if (!st || !g || !r) return null;
  return `${st.toLowerCase()}-${gradeToken(g)}-${r}`;
};

const ROLES = [
  { key: 'educator', label: 'Educator' },
  { key: 'student', label: 'Student (Parent)' },
  { key: 'district', label: 'School/District PO' },
];

export default function Signup() {
  const [state, setState] = useState('');
  // Bumped once the Firestore settings land, purely to force a re-render. The
  // helpers below read a module-level cache synchronously, so the picker needs
  // a nudge after the load resolves. Until then every helper returns the
  // geography.js answer, which is today's behaviour — the picker is never
  // blank or wrong while loading, just one release behind for a moment.
  const [settingsLoaded, setSettingsLoaded] = useState(0);

  useEffect(() => {
    let alive = true;
    loadStateSettings().then(() => {
      if (alive) setSettingsLoaded((n) => n + 1);
    });
    return () => { alive = false; };
  }, []);

  // Derived, not written by hand. The previous copy named Virginia and West
  // Virginia as "live with all four subjects" and went stale the moment that
  // stopped being true — West Virginia has no social studies. Recomputing from
  // settings means the sentence cannot outlive the fact.
  const pickerNote = useMemo(() => {
    const fullyLive = ALL_STATES.filter(
      (s) => isStateAvailable(s) && SUBJECT_KEYS.every((sub) => isSubjectReady(s, sub))
    );
    const partlyLive = ALL_STATES.filter(
      (s) =>
        isStateAvailable(s) &&
        !fullyLive.includes(s) &&
        SUBJECT_KEYS.some((sub) => isSubjectReady(s, sub))
    );

    if (fullyLive.length === ALL_STATES.length) {
      return 'Every state and DC is live with Math, Science and Social Studies, Kindergarten through 5th grade.';
    }

    const parts = [];
    if (fullyLive.length > 2) {
      parts.push(`${fullyLive.length} states are live with Math, Science and Social Studies.`);
    } else if (fullyLive.length) {
      parts.push(
        `${fullyLive.map(stateName).join(' and ')} ${
          fullyLive.length === 1 ? 'is' : 'are'
        } live with Math, Science and Social Studies.`
      );
    }
    if (partlyLive.length) {
      parts.push(
        `${partlyLive.length} more ${partlyLive.length === 1 ? 'state is' : 'states are'} open now, with the remaining subjects on the way.`
      );
    }
    return parts.join(' ');
    // settingsLoaded is the dependency on purpose: the helpers read a module
    // cache, so this has to recompute when that cache is filled.
  }, [settingsLoaded]);

  const stateGroups = useMemo(() => buildStateGroups(), [settingsLoaded]);

  const [grade, setGrade] = useState('');
  const [role, setRole] = useState('');

  const comboReady = Boolean(state && grade && role);

  const product = useMemo(() => {
    if (!comboReady) return null;

    const exact = comboKey(state, grade, role); // "va-5th-educator"
    const stateRoleAny = `${state.toLowerCase()}-all-${role}`; // optional catch-all
    const roleGradeAnyState = `${role}-${grade}`; // optional catch-all

    return (
      PRODUCT_CATALOG[exact] ||
      PRODUCT_CATALOG[stateRoleAny] ||
      PRODUCT_CATALOG[roleGradeAnyState] ||
      FALLBACK_BY_ROLE[role] ||
      null
    );
  }, [comboReady, state, grade, role]);

  const resetAll = () => {
    setState('');
    setGrade('');
    setRole('');
  };

  return (
    <section className="signup-wrapper gradient-color" id="signup" style={{ color: '#fff' }}>
      <div className="overlay"></div>
      <Container>
        <div className="main-title-wrapper" style={{ color: '#fff' }}>
          <Titlespan2 Class="sitemain-subtitle" Name="Choose Your" Label="Plan" />
          <Subtitle
            Class="site-subtitle2"
            Name="Choose your state, grade and role to get started."
          />
          <Description
            Class="video-dec"
            Name="(Choose who's signing up. Anyone under 18 needs a parent or educator to sign them.)"
          />
        </div>

        {/* Pickers */}
        <div className="pickers">
          <div className="picker">
            <label htmlFor="pick-state" className="picker-label">State</label>
            <select
              id="pick-state"
              className="picker-select"
              value={state}
              onChange={(e) => setState(e.target.value)}
              style={{ color: '#000' }}
            >
              <option value="">Select state</option>
              {stateGroups.map((group) => (
                // Grouped rather than one flat list of 51. A parent scanning
                // for their state should be able to see, without reading a
                // note, whether it is live in every subject or partly.
                <optgroup label={group.label} key={group.label}>
                  {group.codes.map((s) => {
                    const ready = SUBJECT_KEYS.filter((sub) => isSubjectReady(s, sub));
                    const open = isStateAvailable(s) && ready.length > 0;

                    // `disabled` rather than hidden. A parent who can't find
                    // their state assumes the site is broken and leaves; one
                    // who sees "coming soon" learns something true and may
                    // come back. A state is selectable the moment it is
                    // offered and has at least one subject with real pages —
                    // the suffix says which, so nobody buys a subject that
                    // isn't there.
                    let suffix = '';
                    if (!open) suffix = ' — coming soon';
                    else if (ready.length < SUBJECT_KEYS.length) suffix = ` — ${joinSubjects(ready)}`;

                    return (
                      <option value={s} key={s} disabled={!open}>
                        {`${stateName(s)} (${s})${suffix}`}
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
            <p className="picker-note">{pickerNote}</p>
          </div>

          <div className="picker">
            <span className="picker-label">Grade</span>
            <div className="grade-scroll" role="group" aria-label="Select grade">
              {GRADES.map((g) => (
                <button
                  type="button"
                  id={`pick-grade-${gradeToken(g)}`}
                  key={g}
                  className={`grade-pill ${grade === g ? 'active' : ''} ${
                    g === 'Pre-K' || g === 'Kindergarten' ? 'grade-pill-wide' : ''
                  }`}
                  onClick={() => setGrade(g)}
                  aria-pressed={grade === g}
                >
                  {/* "Kindergarten" is too wide for a pill next to "3rd".
                      The VALUE stays the full label — that's what
                      GRADE_TOKENS and the role composer key on — only the
                      caption is shortened. */}
                  {g === 'Kindergarten' ? 'K' : g}
                </button>
              ))}
            </div>
          </div>

          <div className="picker">
            <span className="picker-label">Who's signing up?</span>
            <div className="role-scroll" role="group" aria-label="Select role">
              {ROLES.map((r) => (
                <button
                  type="button"
                  id={`pick-role-${r.key}`}
                  key={r.key}
                  className={`role-pill ${role === r.key ? 'active' : ''}`}
                  onClick={() => setRole(r.key)}
                  aria-pressed={role === r.key}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product preview */}
        <div className="product-preview">
          {!comboReady ? (
            <div className="empty">
              <p>
                Select a <strong>State</strong>, <strong>Grade</strong>, and <strong>Role</strong>{' '}
                to see your plan.
              </p>
            </div>
          ) : product ? (
            <div className="card">
              <div className="image">
                <ProductImage
                  // Remount per product so the fallback chain starts over.
                  key={`${product.productNo}-${product.img}`}
                  alt={product.title}
                  sources={[product.img, FALLBACK_BY_ROLE[role]?.img]}
                />
              </div>
              <div className="info">
                <h3 className="title">{product.title}</h3>
                <p className="blurb">{product.blurb}</p>
                <p className="meta">
                  <span className="sku">Product No: {product.productNo}</span>
                  <span className="sep">•</span>
                  <span className="state">State: {state}</span>
                  <span className="sep">•</span>
                  <span className="grade">Grade: {grade}</span>
                </p>

                {/* No price, and no monthly/yearly split.
                    ---------------------------------------------------------
                    This is the top of the funnel — the first thing a visitor
                    sees. Signing up costs nothing (five topics per subject
                    unlock straight away), so showing a subscription price
                    here asks people to weigh a decision they don't have to
                    make yet, and to weigh it before they've seen anything.
                    Billing lives on the plan step, after the account exists
                    and the product has had a chance to sell itself.
                    `product.priceMonthly` / `priceYearly` are still in
                    PRODUCT_CATALOG and still used there — they're just not
                    read on this screen. */}
                <div className="free-cta">
                  <Link
                    className="btn free-start"
                    to={registerHref({ state, grade, role, billing: 'free' })}
                  >
                    {role === 'district'
                      ? 'Request a Quote'
                      : 'Sign Up Free of Charge'}
                  </Link>
                  {role !== 'district' && (
                    <span className="free-note">No card needed</span>
                  )}
                </div>

                <p className="checkout-note">
                  {role === 'district'
                    ? "Create your account first — we'll follow up about seats and purchase orders."
                    : 'Create your account first. Five topics per subject open up right away, at no cost — you can add seats later if you want the full library.'}
                </p>

                {product.toolsIncluded && (
                  <div className="tools-included">
                    {Array.isArray(product.toolsIncluded) ? (
                      <>
                        <strong>Tools included:</strong>
                        <ul>
                          {product.toolsIncluded.map((t, i) => (
                            <li key={i}>{t}</li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p>{product.toolsIncluded}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="empty">
              <p>No product is configured for this combination yet.</p>
            </div>
          )}
        </div>

        <div className="reset-wrap">
          <button type="button" className="btn reset" onClick={resetAll}>
            Reset Combination
          </button>
        </div>
      </Container>
    </section>
  );
}
